import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap, throwError } from 'rxjs';
import type { HttpError } from '@models/http-error.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OpenSessionRequest, SessionInfo, SessionModel } from '@models/session/session.model';
import { Router } from '@angular/router';
import { MealmomentService } from '@services/meal-moment.service';
import { TerminalService } from '@services/terminal.service';
import { CapitalizePipe } from '@pipes/capitalize.pipe';
import { DateService } from '@services/date.service';
import { EnterpriseService } from '@services/enterprise.service';
import { SessionService } from '@services/session.service';
import { AuthService } from '@services/auth.service';
import { AppRoutes } from '@helpers/app-routes';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-open-session-page',
  imports: [TranslateModule, CapitalizePipe],
  templateUrl: './open-session-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'block h-full',
  },
})
export class OpenSessionPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mealMomentService = inject(MealmomentService);
  private readonly terminalService = inject(TerminalService);
  private readonly enterpriseService = inject(EnterpriseService);
  private readonly dateService = inject(DateService);
  private readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  protected readonly date = computed(() =>
    this.dateService.format(new Date(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  );

  protected readonly lastSessionDate = computed(() => {
    const session = this.sessionService.lastSession();
    if (!session?.closed_at) {
      return null;
    }

    try {
      const date = new Date(session.closed_at);
      return this.dateService.format(date, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch (error) {
      return null;
    }
  });

  protected readonly sessionInfo = computed<SessionInfo>(() => ({
    cashRegisterName: this.terminalService.currentTerminal()?.label ?? '',
    locationName: this.enterpriseService.currentEnterprise()?.name ?? '',
    serviceName: this.mealMomentService.currentMealMoment()?.name ?? '',
  }));

  ngOnInit(): void {
    const enterpriseId = this.terminalService.currentTerminal()?.enterprise_id;
    if (enterpriseId) {
      this.enterpriseService.getEnterprise(enterpriseId).subscribe();
    }

    this.sessionService.getLastSession().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  protected openSession(): void {
    const terminalId = this.terminalService.currentTerminal()?.id;
    const userId = this.authService.getCurrentUser()?.id;

    if (!terminalId || !userId) return;

    const request: OpenSessionRequest = {
      self_pos_terminal_id: terminalId,
      opened_by_user_id: userId,
    };

    this.sessionService
      .getLastSession()
      .pipe(
        switchMap((session: SessionModel) =>
          session.status === 'open' ? of(session) : this.sessionService.openSession(request),
        ),
        catchError((err: HttpError) => {
          if (err.message === 'errors.self_pos_terminal_session.not_found') {
            return this.sessionService.openSession(request);
          }
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => void this.router.navigate([AppRoutes.scanPage]),
        error: (error) => {
          const translatedMessage = this.translate.instant(error.message);

          if (translatedMessage !== error.message) {
            this.toastr.error(
              translatedMessage,
              this.translate.instant('session.toast.error_title'),
            );
          } else {
            this.toastr.error(
              this.translate.instant('session.toast.error_message'),
              this.translate.instant('session.toast.error_title'),
            );
          }
        },
      });
  }
}
